import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Typography,
  Descriptions,
  Space,
  Button,
  Popover,
  Divider,
  Skeleton,
  message,
  Modal,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DownloadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

import {
  Header,
  EditableDescriptionItem,
  ProductTable,
  PaymentInfoCard,
  DeliveryInfoCard,
} from "../components";
import { fetchOrderByID, editOrderByID } from "../utils/fetchOrders";
import { fetchSellers } from "../utils/fetchSellers";
import {
  createBoleta,
  createNotaDeCredito,
  fetchOrderDocuments,
  downloadDocument,
} from "../utils/fetchReceipts";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

const { Title } = Typography;

export const comunas = [
  "LA FLORIDA",
  "LA PINTANA",
  "PUENTE ALTO",
  "SAN BERNARDO",
  "LA REINA",
  "LAS CONDES",
  "PROVIDENCIA",
  "SANTIAGO CENTRO",
  "VITACURA",
  "LO BARNECHEA",
  "CERRO NAVIA",
  "CONCHALI",
  "HUECHURABA",
  "INDEPENDENCIA",
  "QUILICURA",
  "RECOLETA",
  "RENCA",
  "CERRILLOS",
  "ESTACION CENTRAL",
  "LO PRADO",
  "MAIPU",
  "PUDAHUEL",
  "QUINTA NORMAL",
  "EL BOSQUE",
  "LA GRANJA",
  "PEDRO AGUIRRE CERDA",
  "SAN JOAQUIN",
  "SAN MIGUEL",
  "SAN RAMON",
  "LA CISTERNA",
  "LO ESPEJO",
  "MACUL",
  "ÑUÑOA",
  "PEÑALOLEN",
  "PEÑAFLOR",
  "PADRE HURTADO",
];

const centeredLabelStyle = {
  display: "flex",
  alignItems: "center",
  height: "100%",
};

const { format } = new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
});

const OrderDetails = () => {
  const navigate = useNavigate();
  const baseUrl = `${window.location.protocol}//${window.location.host}`;
  const { id } = useParams(); // order_id

  const { user } = useAuth();
  const canWriteOrders = hasPermission(user?.permissions, "orders:write");
  const canReadPayments = hasPermission(user?.permissions, "payments:read");
  const canReadDeliveries = hasPermission(user?.permissions, "deliveries:read");
  const canReadReceipts = hasPermission(user?.permissions, "receipts:read");
  const canWriteReceipts = hasPermission(user?.permissions, "receipts:write");

  const [isCopied, setIsCopied] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  //   const [order, setOrder] = useState(infoOrder);

  const [orderData, setOrderData] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [sellers, setSellers] = useState(null);

  const isEditingDefault = id === "create"; // make it true when create

  // Billing state
  const [documents, setDocuments] = useState([]);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [isCreateBoletaModalOpen, setIsCreateBoletaModalOpen] = useState(false);
  const [isCreateCreditNoteModalOpen, setIsCreateCreditNoteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const docTypeConfig = {
    BOLETA: { label: "Boleta", color: "cyan" },
    "NOTA DE CRÉDITO": { label: "Nota de crédito", color: "orange" },
  };

  // newest-first — documents[0] is the latest
  const latestDocType = documents[0]?.document_type ?? null;
  const latestBoleta = documents.find((d) => d.document_type === "BOLETA");

  useEffect(() => {
    if (id === "create" || !canReadReceipts) return;
    setIsDocumentsLoading(true);
    fetchOrderDocuments(id)
      .then(setDocuments)
      .catch(() => {})
      .finally(() => setIsDocumentsLoading(false));
  }, [id]);

  const refreshDocuments = async () => {
    const docs = await fetchOrderDocuments(id);
    setDocuments(docs);
  };

  const handleCreateBoleta = async () => {
    setIsCreating(true);
    try {
      await createBoleta(id);
      await refreshDocuments();
      setIsCreateBoletaModalOpen(false);
      message.success("Boleta emitida correctamente.");
    } catch (error) {
      message.error(
        error.response?.data?.message || "Error al emitir la boleta.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateCreditNote = async () => {
    setIsCreating(true);
    try {
      await createNotaDeCredito(latestBoleta.id);
      await refreshDocuments();
      setIsCreateCreditNoteModalOpen(false);
      message.success("Nota de crédito creada correctamente.");
    } catch (error) {
      message.error(
        error.response?.data?.message || "Error al crear la nota de crédito.",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      await downloadDocument(doc.id, doc.document_id);
    } catch (error) {
      message.error(error.message || "Error al descargar el PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (id === "create") {
      setIsDataLoading(false);
      // handle creation
    } else {
      setIsDataLoading(true);
      const fetchData = async () => {
        try {
          const vals = await fetchOrderByID(id);
          const valsSellers = await fetchSellers();
          console.log("this is valsSellers", valsSellers);
          setOrderData(vals[0]);
          setSellers(valsSellers);
          setIsDataLoading(false);
          console.log("Estos son los valotres de val");
          console.log(vals[0]);
        } catch (error) {
          console.log(error);
          setOrderData(null);
          setDataError(error);
          setIsDataLoading(false);
        }
      };
      fetchData();
    }
  }, []);

  const handleSave = async (key, newValue) => {
    messageApi.open({
      type: "loading",
      content: "Actualizando información..",
      duration: 0,
    });
    try {
      const newData = { ...orderData, [key]: newValue };
      const result = await editOrderByID(newData);
      setOrderData((prevOrderData) => ({
        ...prevOrderData,
        [key]: newValue,
      }));
      messageApi.destroy();
      message.success("Información actualizada correctamente.");
    } catch (error) {
      messageApi.destroy();
      message.error("Error al actualizar la información.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(orderData.raw).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000); // Reset after 2 seconds
    });
  };

  return (
    <div className="">
      {contextHolder}
      <div className="mb-5 flex justify-between">
        <Header category="Página" title="Pedidos" />
      </div>
      <div className="md:grid w-full h-full md:grid-cols-2 grid-cols-1 gap-4 space-y-4 md:space-y-0">
        <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4 md:p-6">
          <Title level={4}>General</Title>
          <Divider style={{ margin: "4px" }} />
          {isDataLoading ? (
            <>
              <Skeleton active title={false} paragraph={{ rows: 4 }} />
            </>
          ) : (
            <>
              <Descriptions
                column={{ xxl: 2, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
                className="mt-4"
              >
                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Numero</div>}
                >
                  <EditableDescriptionItem
                    label="daily_id"
                    value={orderData.daily_id}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("daily_id", newValue)}
                    numericInput={true}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Nombre</div>}
                >
                  <EditableDescriptionItem
                    label="Name"
                    value={orderData.name}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("name", newValue)}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Dirección</div>}
                >
                  <EditableDescriptionItem
                    label="Address"
                    value={orderData.address}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("address", newValue)}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Comuna</div>}
                >
                  <EditableDescriptionItem
                    label="City"
                    value={orderData.city}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("city", newValue)}
                    suggestions={comunas}
                  />
                </Descriptions.Item>

                <Descriptions.Item
                  label={
                    <div style={centeredLabelStyle}>Tipo de Documento</div>
                  }
                >
                  <EditableDescriptionItem
                    label="order_type"
                    value={orderData.order_type}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("order_type", newValue)}
                    suggestions={["Factura", "Boleta"]}
                  />
                </Descriptions.Item>

                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Vendedor</div>}
                >
                  <EditableDescriptionItem
                    label="seller_id"
                    value={
                      sellers.filter(
                        (item) => item.personnel_id === orderData.seller_id,
                      )[0]?.name || "No asignado"
                    }
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) =>
                      handleSave(
                        "seller_id",
                        sellers.filter((item) => item.name === newValue)[0]
                          .personnel_id,
                      )
                    }
                    suggestions={sellers.map((item) => item.name)}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={<div style={centeredLabelStyle}>Comuna</div>}
                >
                  <EditableDescriptionItem
                    label="Phone"
                    value={orderData.phone_number}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("phone_number", newValue)}
                  />
                </Descriptions.Item>
              </Descriptions>

              {orderData.order_source === "Whatsapp" && (
                <>
                  <Popover
                    placement="right"
                    title={orderData.order_source}
                    content={
                      <div>
                        {orderData.raw.split("\n").map((item) => (
                          <p>{item}</p>
                        ))}
                      </div>
                    }
                  >
                    <Button>Ver Mensaje Original</Button>
                  </Popover>
                  <Button onClick={handleCopy}>
                    {isCopied ? "Copiado!" : "Copiar Mensaje"}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
        <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4">
          <Title level={4}>Productos</Title>
          <Divider style={{ margin: "4px" }} />
          {isDataLoading ? (
            <>
              <Skeleton active title={false} paragraph={{ rows: 4 }} />
            </>
          ) : (
            <>
              <ProductTable
                order_id={id}
                products={orderData.products}
                setProducts={(newProducts) =>
                  setOrderData((prevOrderData) => ({
                    ...prevOrderData,
                    products: newProducts,
                  }))
                }
                totalAmount={orderData.amount}
                updateTotalOrderAmount={(price) =>
                  handleSave("amount", orderData.amount + price)
                }
              />
              <div className="flex w-full justify-end mt-5 items-center gap-3">
                <div className="text-gray-500 text-md">Monto Total:</div>
                <Typography.Text strong>
                  <EditableDescriptionItem
                    label="amount"
                    value={orderData.amount}
                    isEditingDefault={isEditingDefault}
                    editable={canWriteOrders}
                    onSave={(newValue) => handleSave("amount", newValue)}
                    numericInput={true}
                    format={format}
                  />
                </Typography.Text>
              </div>
            </>
          )}
        </div>
        {canReadPayments && (
          <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4">
            <Title level={4}>Información de Pago</Title>
            {isDataLoading ? (
              <>
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              </>
            ) : (
              <>
                <PaymentInfoCard
                  paymentInfo={orderData.payment_info_array}
                  setPaymentInfo={(newPayments) =>
                    setOrderData((prevOrderData) => ({
                      ...prevOrderData,
                      payment_info_array: newPayments,
                    }))
                  }
                  orderID={id}
                  deliveryInfo={orderData.delivery_info_array}
                />
              </>
            )}
          </div>
        )}
        {canReadDeliveries && (
          <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4">
            <Title level={4}>Información de Entrega</Title>
            {isDataLoading ? (
              <>
                <Skeleton active title={false} paragraph={{ rows: 4 }} />
              </>
            ) : (
              <>
                <DeliveryInfoCard
                  deliveryInfo={orderData.delivery_info_array}
                  setDeliveryInfo={(newDeliveries) =>
                    setOrderData((prevOrderData) => ({
                      ...prevOrderData,
                      delivery_info_array: newDeliveries,
                    }))
                  }
                  orderID={id}
                />
              </>
            )}
          </div>
        )}

        {canReadReceipts && (
        <div className="bg-white dark:bg-main-dark-bg rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileTextOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Facturación
            </Title>
          </div>
          <Divider style={{ margin: "8px 0 12px" }} />
          {isDocumentsLoading ? (
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          ) : (
            <>
              {documents.length === 0 ? (
                <Typography.Text type="secondary" className="block text-center py-2">
                  Sin documento emitido
                </Typography.Text>
              ) : (
                <div className="flex flex-col gap-2 mb-3">
                  {documents.map((doc) => {
                    const cfg = docTypeConfig[doc.document_type] ?? {
                      label: doc.document_type,
                      color: "default",
                    };
                    return (
                      <div key={doc.id} className="flex justify-between items-center px-1">
                        <Space>
                          <Tag color={cfg.color}>{cfg.label}</Tag>
                          <Typography.Text>N° {doc.document_id}</Typography.Text>
                          <Typography.Text type="secondary">
                            {new Date(doc.created_at).toLocaleDateString("es-ES", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })}
                          </Typography.Text>
                        </Space>
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          loading={downloadingId === doc.id}
                          disabled={false}
                          onClick={() => handleDownload(doc)}
                          size="small"
                        >
                          PDF
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {canWriteReceipts && (
                <>
                  <Divider style={{ margin: "8px 0" }} />

                  <div className="flex justify-center">
                    {latestDocType === "BOLETA" ? (
                      <Button
                        icon={<FileTextOutlined />}
                        onClick={() => setIsCreateCreditNoteModalOpen(true)}
                      >
                        Crear nota de crédito
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCreateBoletaModalOpen(true)}
                      >
                        Emitir boleta
                      </Button>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <Modal
            title="Emitir boleta"
            open={isCreateBoletaModalOpen}
            onCancel={() => setIsCreateBoletaModalOpen(false)}
            onOk={handleCreateBoleta}
            okText="Emitir"
            cancelText="Cancelar"
            confirmLoading={isCreating}
            width={360}
          >
            <Typography.Text>
              Se emitirá una boleta electrónica para este pedido en Defontana.
            </Typography.Text>
          </Modal>

          <Modal
            title="Crear nota de crédito"
            open={isCreateCreditNoteModalOpen}
            onCancel={() => setIsCreateCreditNoteModalOpen(false)}
            onOk={handleCreateCreditNote}
            okText="Crear"
            cancelText="Cancelar"
            confirmLoading={isCreating}
            width={360}
          >
            <Typography.Text>
              Se creará una nota de crédito para anular la boleta{" "}
              <Typography.Text strong>N° {latestBoleta?.document_id}</Typography.Text>.
            </Typography.Text>
          </Modal>
        </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
